import { mqMin, space, tok } from '@sunwebgroup/ui-components'
import { Button, Icon, Typography } from '@sunwebgroup/ui-components/uds'
import { use } from 'react'
import * as React from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import Layout from '@/scopes/prices/components/Layout'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTable/NavigationByWeeksContext'
import { alternativeFilterSelector } from '@/scopes/prices/components/selectors'
import { FiltersInPricesValues, PricesFilterType, SingleSelectFiltersKeys } from '@/scopes/prices/domain/filters/types'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const WarningIcon = styled(Icon)`
  color: ${tok('primitives_color_warning_300')};
  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Icon.modSize(48)}
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')};
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_4')};
`

const BoldChip = styled(Button)``

const singleSelectNormalizedKeys = new Map(
  SingleSelectFiltersKeys.map(singleSelectKey => [singleSelectKey.toString(), singleSelectKey]),
)

export function AlternativeFilters() {
  const intl = useIntl()
  const { resetPrices } = use(PricesActionsContext)
  const { reset: resetNavigationContext } = React.use(NavigationByWeeksContext)

  const reset = async () => {
    resetNavigationContext()
    await resetPrices({
      initialFilterSelection: {
        Participants: [
          {
            adults: 2,
            children: [],
            babies: [],
          },
        ],
      },
    })
  }

  return (
    <Layout.Root>
      <WarningIcon glyph="ExclamationInTriangle" size={40} />
      <Layout.Content>
        <ContentWrapper>
          <Layout.TitleDescription>
            <Typography variant="body-l-bold-tight">
              {intl.formatMessage({ id: 'prices_alternative_filters_title' })}
            </Typography>
            <Typography variant="body-m-regular-tight">
              {intl.formatMessage({ id: 'prices_alternative_filters_description' })}
            </Typography>
          </Layout.TitleDescription>
          <AlternativeFilter filterType="Month" />
          <AlternativeFilter filterType="Duration" />
          <AlternativeFilter filterType="DepartureAirport" />
          <AlternativeFilter filterType="RoomType" />
          <AlternativeFilter filterType="Mealplan" />
          <AlternativeFilter filterType="TransportType" />
          <Wrapper>
            <Typography>{intl.formatMessage({ id: 'prices_alternative_filters_reset_filters_title' })}</Typography>
            <div>
              <BoldChip variant="secondary" onClick={() => reset()}>
                {intl.formatMessage({ id: 'prices_alternative_filters_reset_filters_description' })}
              </BoldChip>
            </div>
          </Wrapper>
        </ContentWrapper>
      </Layout.Content>
    </Layout.Root>
  )
}

const ChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space('primitives_space_12')};
`

function AlternativeFilter({ filterType }: { filterType: PricesFilterType }) {
  const data = usePricesSelector(alternativeFilterSelector, filterType)
  const { navigateToMonth } = use(NavigationByWeeksContext)
  const { selectFilter, selectFilterMonth } = use(PricesActionsContext)
  const setSelection = (selection: FiltersInPricesValues) => {
    let newSelection = selection
    if (singleSelectNormalizedKeys.has(filterType)) {
      // SingleSelect
      newSelection = selection![0] as FiltersInPricesValues
    }
    if (filterType === 'Month') {
      selectFilterMonth(newSelection as string, navigateToMonth)
    }
    else {
      selectFilter({ type: filterType, selection: newSelection })
    }
  }

  if (!data)
    return null

  return (
    <Wrapper>
      <Typography>{data.title}</Typography>
      <ChipsWrapper>
        {data.options.map(option => (
          <BoldChip variant="secondary" size="M" key={option.value} value={option.value} onClick={() => setSelection([option.value])}>
            {option.text}
          </BoldChip>
        ))}
      </ChipsWrapper>
    </Wrapper>
  )
}
